import { NON_CONTEXTUAL_PACKAGE_NAME } from '../../../../constants/formConstants';
import { GP_SLOT_VALUES } from '../../constants/DetailedBookingConstants';
import { FORM_FIELDS } from '../../constants/FormFieldsConstants';
import {
    SlotOptionType, SlotPackage, SovActiveModalType
} from '../../interface/DetailedBookingInterface';

export const getSelectedSlotData = (
    gpSlot: string,
    slotPackagesData: SlotPackage[],
    selectedOption?: SlotOptionType
) => {
    if (!gpSlot) return null;
    if (selectedOption?.type === GP_SLOT_VALUES.NON_CONTEXTUAL) {
        for (const pkg of slotPackagesData) {
            if (pkg.slotPackageName.toLowerCase() === NON_CONTEXTUAL_PACKAGE_NAME) {
                const segment = pkg.segments.find(
                    ({ segmentName }) => segmentName === gpSlot
                );
                if (segment) return segment;
            }
        }
    }

    return null;
};

/**
 * Returns the effective available percentage for a slot, accounting for the
 * booking's own existing allocation in edit mode.
 */
export const getEffectivePercentageAvailable = (
    gpSlot: string,
    slotPackagesData: SlotPackage[],
    selectedOption?: SlotOptionType,
    existingPercentage?: number,
): number => {
    const slotData = getSelectedSlotData(gpSlot, slotPackagesData, selectedOption);
    const rawAvailable = slotData?.percentageAvailable || 0;
    return rawAvailable + (existingPercentage || 0);
};

/**
 * Returns the effective available percentage for a contextual node, accounting
 * for the booking's own existing allocation in edit mode.
 */
export const getEffectiveContextualPercentageAvailable = (
    rawAvailable: number,
    existingPercentage?: number,
): number => {
    return rawAvailable + (existingPercentage || 0);
};

export const checkRoValueValidation = (valueBlockedByLineItem: number, roValue: number, setActiveModal: SovActiveModalType) => {
    if (valueBlockedByLineItem > roValue && roValue > 0) {
        setActiveModal(FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM);
        return true;
    }
    return false;
};

export const checkPercentageValidation = (
    sovPercentage: number,
    setActiveModal: SovActiveModalType,
    gpSlot: string,
    slotPackagesData: SlotPackage[],
    selectedOption?: SlotOptionType,
    isApprovedBooking?: boolean,
    existingPercentage?: number,
    isWaitlistEnabled?: boolean,
) => {
    // Waitlist (Art Event value 1): a request may exceed available SOV on purpose — the backend
    // allocates what is available and queues the rest. Skip only this availability gate; the value/RO
    // budget gate still applies.
    if (isWaitlistEnabled) {
        return false;
    }
    const selectedSlotData = getSelectedSlotData(gpSlot, slotPackagesData, selectedOption);
    if (
        selectedOption?.type === GP_SLOT_VALUES.NON_CONTEXTUAL &&
        selectedSlotData &&
        sovPercentage > selectedSlotData.percentageAvailable
    ) {
        if (isApprovedBooking && existingPercentage !== undefined && sovPercentage <= (selectedSlotData.percentageAvailable + existingPercentage)) return false;
        setActiveModal(FORM_FIELDS.SOV_PERCENTAGE_FOR_GP);
        return true;
    }
    return false;
};

export const checkContextualPercentageValidation = (
    sovPercentage: number,
    setActiveModal: SovActiveModalType,
    contextualNodeName: string,
    gpSlot: string,
    slotPackagesData: SlotPackage[],
    selectedOption?: SlotOptionType,
    setContextualPercentageAvailable?: React.Dispatch<React.SetStateAction<number>>,
    isApprovedBooking?: boolean,
    existingPercentage?: number,
    isWaitlistEnabled?: boolean,
) => {
    // Waitlist: over-requesting a contextual segment is allowed (backend queues the excess).
    if (isWaitlistEnabled) {
        return false;
    }
    if (selectedOption?.type === GP_SLOT_VALUES.CONTEXTUAL && contextualNodeName) {
        const selectedPackageData = slotPackagesData.find(
            (pkg) => pkg.slotPackageName === gpSlot
        );
        const selectedSegment = selectedPackageData?.segments.find(
            ({ segmentName }) => segmentName === contextualNodeName
        );

        if (selectedSegment && sovPercentage > selectedSegment.percentageAvailable) {
            if (isApprovedBooking && existingPercentage !== undefined && sovPercentage <= (selectedSegment.percentageAvailable + existingPercentage)) return false;
            setContextualPercentageAvailable?.(selectedSegment.percentageAvailable);
            setActiveModal(FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP);
            return true;
        }
    }
    return false;
};

export const checkContextualValueValidation = (
    valueBooked: number,
    roValue: number,
    setActiveModal: SovActiveModalType
) => {
    if (valueBooked > roValue && roValue > 0) {
        setActiveModal(FORM_FIELDS.VALUE_BOOKED);
        return true;
    }
    return false;
};
